/**
 * Translates what the game does into what the player hears.
 *
 * The director subscribes to core events for everything the simulation already
 * announces (locks, clears, levels, game over) and diffs public state for the
 * things it does not (a move that was blocked, a rotation that kicked, a row
 * ticking past under soft drop, a menu cursor moving).
 *
 * It holds no browser types: it takes a `SoundBoard` and reads plain state, so
 * the whole mapping is exercised in Node against a recording stub.
 */

import { Action, GameState, Phase, RuleSet } from '../core/game.ts';
import type { LineClearEvent } from '../core/game.ts';
import type { SoundBoard } from './board.ts';
import type { Cue } from './cues.ts';

/** How loaded the platform has to get before BALANCE starts beeping. */
const BALANCE_WARN_AT = 0.8;
/** ...and how far it has to come back before the beep can arm again. */
const BALANCE_REARM_AT = 0.65;

/** The interface state the director listens to. Structural, so `Shell` fits. */
export interface UiSnapshot {
  screen: string;
  /** The menu cursor. A number in the shell today; compared, never read. */
  row: string | number;
  mode: string;
  filter: string;
  theme: string;
  name: string;
}

/** Anything shaped like the shell can be sampled without importing it. */
export interface UiSource {
  screen: string;
  menuRow: string | number;
  gameMode: string;
  boardFilter: string;
  theme: string;
  nameDraft: string;
}

export function readUi(source: UiSource): UiSnapshot {
  return {
    screen: source.screen,
    row: source.menuRow,
    mode: source.gameMode,
    filter: source.boardFilter,
    theme: source.theme,
    name: source.nameDraft,
  };
}

interface Probe {
  phase: Phase;
  x: number;
  y: number;
  rotation: number;
  hasPiece: boolean;
}

/** Per-game state the director has to remember between calls. */
interface Channel {
  game: GameState;
  pitch: number;
  probe: Probe;
  /** Row the active piece occupied at the last step, for soft-drop ticks. */
  dropY: number | null;
  holdFired: boolean;
  /** Set when the current dispatch already announced a fresh run. */
  startFired: boolean;
  balanceWarned: boolean;
}

export interface AttachOptions {
  /** Frequency multiplier for this game's cues. TWIN uses it to separate boards. */
  pitch?: number;
}

export class AudioDirector {
  private readonly channels: Channel[] = [];
  private played = false;

  constructor(private readonly board: SoundBoard) {}

  /** Subscribe to a game's events. Returns an unsubscribe function. */
  attach(game: GameState, options: AttachOptions = {}): () => void {
    const channel: Channel = {
      game,
      pitch: options.pitch ?? 1,
      probe: probe(game),
      dropY: null,
      holdFired: false,
      startFired: false,
      balanceWarned: false,
    };
    this.channels.push(channel);

    const offs = [
      game.events.on('onPhaseChange', (event) => this.onPhaseChange(channel, event.from, event.to)),
      game.events.on('onLock', (event) => {
        this.emit(event.hardDrop ? 'piece.hardDrop' : 'piece.lock', channel);
        channel.dropY = null;
      }),
      game.events.on('onLineClear', (event) => this.onLineClear(channel, event)),
      game.events.on('onLevelUp', () => this.emit('game.levelUp', channel)),
      game.events.on('onHold', () => {
        channel.holdFired = true;
        this.emit('piece.hold', channel);
      }),
      game.events.on('onGameOver', (event) => {
        this.emit(event.reason === 'topple' ? 'game.topple' : 'game.over', channel);
      }),
    ];

    return () => {
      for (const off of offs) off();
      const index = this.channels.indexOf(channel);
      if (index >= 0) this.channels.splice(index, 1);
    };
  }

  // ------------------------------------------------------------------ input

  /** Sample every attached game just before an action is applied. */
  beforeInput(): void {
    for (const channel of this.channels) {
      channel.probe = probe(channel.game);
      channel.holdFired = false;
      channel.startFired = false;
    }
  }

  /**
   * Sound the difference the action made. Locks, clears and phase changes come
   * through events; what is left here is the feedback for moves that succeeded,
   * moves that hit a wall, and a hold that was refused.
   */
  afterInput(action: Action): void {
    for (const channel of this.channels) {
      const before = channel.probe;
      const after = probe(channel.game);

      // Restarting a live run leaves the phase on PLAYING, so nothing else
      // reports it. Restarting a finished one already sounded via the phase.
      if (action === Action.Restart) {
        if (!channel.startFired) this.startedRun(channel);
        continue;
      }

      // A key that woke a READY board only starts the run; it is not a move.
      if (before.phase !== Phase.Playing || after.phase !== Phase.Playing) continue;

      switch (action) {
        case Action.MoveLeft:
        case Action.MoveRight:
          if (after.x !== before.x) this.emit('piece.move', channel);
          else if (after.hasPiece) this.emit('piece.bump', channel);
          break;
        case Action.RotateCW:
        case Action.RotateCCW:
        case Action.Rotate180:
          if (after.rotation !== before.rotation) this.emit('piece.rotate', channel);
          else if (after.hasPiece) this.emit('piece.bump', channel);
          break;
        case Action.Hold:
          if (!channel.holdFired && after.hasPiece) this.emit('ui.deny', channel);
          break;
        default:
          break;
      }
    }
  }

  /**
   * Called once per frame after the simulation advances. Covers the two things
   * no event reports: rows ticking past under soft drop, and a BALANCE platform
   * leaning far enough to deserve a warning.
   */
  afterStep(): void {
    for (const channel of this.channels) {
      const game = channel.game;
      const piece = game.active;

      if (!piece || game.phase !== Phase.Playing) {
        channel.dropY = null;
      } else {
        if (channel.dropY !== null && piece.y > channel.dropY && game.isSoftDropping()) {
          this.emit('piece.softDrop', channel);
        }
        channel.dropY = piece.y;
      }

      if (game.rules !== RuleSet.Balance || game.phase !== Phase.Playing) continue;
      const risk = game.balanceState().risk;
      if (!channel.balanceWarned && risk >= BALANCE_WARN_AT) {
        channel.balanceWarned = true;
        this.emit('balance.warn', channel);
      } else if (channel.balanceWarned && risk < BALANCE_REARM_AT) {
        channel.balanceWarned = false;
      }
    }
  }

  // --------------------------------------------------------------------- ui

  /**
   * Sound a change in the interface. Returns true when something was played, so
   * the host can fall back to a generic click for a control that changed
   * nothing visible.
   */
  uiChanged(before: UiSnapshot, after: UiSnapshot): boolean {
    let sounded = false;

    if (before.theme !== after.theme) {
      this.emit('ui.toggle');
      sounded = true;
    }

    if (before.screen !== after.screen) {
      // Entering the game is announced by the run starting, not by the screen.
      if (after.screen === 'MENU') this.emit('ui.back');
      else if (after.screen === 'SCOREBOARD') this.emit('ui.select');
      return true;
    }

    if (
      before.row !== after.row ||
      before.mode !== after.mode ||
      before.filter !== after.filter ||
      before.name !== after.name
    ) {
      this.emit('ui.move');
      sounded = true;
    }

    return sounded;
  }

  /** Play a cue directly. For the host's own controls (mute, generic clicks). */
  play(cue: Cue): void {
    this.emit(cue);
  }

  /** Clear the "something was heard" flag before dispatching an interaction. */
  mark(): void {
    this.played = false;
  }

  /** Whether any cue has been played since the last `mark()`. */
  get sounded(): boolean {
    return this.played;
  }

  // ---------------------------------------------------------------- private

  private onPhaseChange(channel: Channel, from: Phase, to: Phase): void {
    if (to === Phase.Playing) {
      if (from === Phase.Paused) {
        this.emit('game.resume', channel);
        return;
      }
      this.startedRun(channel);
      return;
    }
    if (to === Phase.Paused) this.emit('game.pause', channel);
    // GAME_OVER is left to `onGameOver`, which knows why the run ended.
  }

  private startedRun(channel: Channel): void {
    this.emit('game.start', channel);
    channel.startFired = true;
    channel.balanceWarned = false;
    channel.dropY = null;
  }

  private onLineClear(channel: Channel, event: LineClearEvent): void {
    let delay = 0;

    if (event.tspin !== 'none') {
      this.emit('clear.tspin', channel, { pitch: event.tspin === 'mini' ? 1.19 : 1 });
      delay = 0.09;
    }

    if (event.lines > 0) {
      this.emit(CLEAR_CUES[event.lines] ?? 'clear.single', channel, { delay });
    }

    if (event.backToBack) this.emit('clear.backToBack', channel, { delay: delay + 0.16 });

    if (event.combo >= 1) {
      // Every link in the chain lifts the answer a step, capped so a long combo
      // does not end up inaudible.
      const step = Math.min(event.combo, 9);
      this.emit('clear.combo', channel, { pitch: 1 + step * 0.085, delay: delay + 0.2 });
    }
  }

  private emit(
    cue: Cue,
    channel?: Channel,
    options: { pitch?: number; gain?: number; delay?: number } = {},
  ): void {
    this.played = true;
    this.board.play(cue, {
      ...options,
      pitch: (options.pitch ?? 1) * (channel?.pitch ?? 1),
    });
  }
}

const CLEAR_CUES: Record<number, Cue> = {
  1: 'clear.single',
  2: 'clear.double',
  3: 'clear.triple',
  4: 'clear.tetris',
};

function probe(game: GameState): Probe {
  const piece = game.active;
  return {
    phase: game.phase,
    x: piece?.x ?? 0,
    y: piece?.y ?? 0,
    rotation: piece?.rotation ?? 0,
    hasPiece: piece !== null,
  };
}
